import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useController, Control } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import debounce from 'lodash/debounce';
import _noop from 'lodash/noop';

import GeoAutocompleteNext from '@ott/geo-autocomplete-next';
import { l10n } from '@ott/l10n';

import { Action, ThunkDispatch } from '@reduxjs/toolkit';

import { trackingTypes } from '../../constants';
import { SharedState, trackingTypes as suggestTracking } from '../../redux/reducer';
import { actions as popularDestinationsActions } from '../../redux/reducers/popularDestinations';
import { actions as suggestsActions } from '../../redux/reducers/suggests';
import suggestSelector from '../../selectors/suggestSelector';
import { Destination, FormState, RenderContext } from '../../types';

interface Props {
  control: Control<FormState>;
  hapi?: string;
  initialTriggerValue: string;
  triggerClassName: string;
  context: RenderContext;
  hasInitialDestination: boolean;
}

const DestinationField: React.FC<Props> = (props) => {
  const { control, hapi, initialTriggerValue, triggerClassName, context, hasInitialDestination } =
    props;
  const dispatch = useDispatch<ThunkDispatch<SharedState, void, Action>>();
  const { hasPopularDestinations, list, isLoading } = useSelector(suggestSelector);
  const [triggerValue, setTriggerValue] = useState(initialTriggerValue);

  // eslint-disable-next-line consistent-return
  const fetchInitialItem = async (requested = '') => {
    try {
      const [first] = await dispatch(
        suggestsActions.fetch({
          hapi,
          requested,
          context,
        })
      ).unwrap();

      return first;
    } catch (error) {
      console.error('[hotels-search-form] Cannot fetch initial item', error);
    }
  };

  const fetchSuggest = useCallback(
    async (requested = '') => {
      if (requested.length === 0) {
        if (!hasPopularDestinations) {
          dispatch(popularDestinationsActions.fetch({ hapi }));
        }

        dispatch(suggestsActions.reset());

        return;
      }

      if (requested.length > 1) {
        try {
          const suggestItems = await dispatch(
            suggestsActions.fetch({
              hapi,
              requested,
              context,
            })
          ).unwrap();

          if (suggestItems.length < 1) {
            dispatch({ type: suggestTracking.suggestsEmpty });
          }
        } catch (error) {
          dispatch({ type: suggestTracking.suggestsEmpty });
        }
      }
    },
    [dispatch, hasPopularDestinations, hapi, context]
  );

  const fetchSuggestDebounced = useCallback(debounce(fetchSuggest, 300), [fetchSuggest]);

  const {
    field: { value, onChange, onBlur },
  } = useController({
    name: 'to',
    control,
  });

  useEffect(() => {
    dispatch(popularDestinationsActions.fetch({ hapi }));

    if (triggerValue) {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      fetchInitialItem(triggerValue).then((value) => {
        if (hasInitialDestination) {
          return;
        }

        onChange(value);
      });
    }
  }, []);

  const onChangeWithTracking = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-shadow
    (value: Destination) => {
      onChange(value);

      if (!value) {
        return;
      }

      const { id: selection, name: selectionName, type: selectionType } = value;
      const selectionOrder = list.indexOf(value);

      dispatch({
        type: trackingTypes.destinationChanged,
        eventData: {
          context,
          selection,
          selectionName,
          selectionType,
          selectionOrder,
        },
      });
    },
    [onChange, dispatch, context, list]
  );

  const triggerProps = useMemo(() => {
    return {
      name: 'to',
      className: triggerClassName,
      withFullMask: true,
      onChange: setTriggerValue,
      value: triggerValue,
      onSearch: fetchSuggestDebounced,
      onBlur,
      placeholder: l10n('hotelsSearchForm.suggestPlaceholder'),
      title: l10n('hotelsSearchForm.suggestPlaceholder'),
      autoSelectOnFocus: true,
      'data-locator': 'destination-field-input',
    };
  }, [onBlur, triggerValue, triggerClassName, fetchSuggestDebounced]);

  const overlayProps = useMemo(() => {
    return {
      options: list,
      shouldUseEllipsis: true,
    };
  }, [list]);

  return (
    <GeoAutocompleteNext
      revertChanges={true}
      overlayProps={overlayProps}
      triggerProps={triggerProps}
      onChange={onChangeWithTracking}
      value={value}
      isLoading={isLoading}
    />
  );
};

export default DestinationField;
